import axios from '~src/api/axios';
import { stringify } from 'query-string';
import {
  APP_IDS,
  COMPONENT_IDS,
  PAGE_IDS,
  QUIZ_IDS,
  UiIds,
} from '~constants/index';
import { URLS } from '~src/api/urls';
import withCookieData from '~utils/withCookieData';
import { ObjectType } from '~entities/Object';
import analytics from '~api/analytics';
import { arrayContains, arrayEquals } from '~api/helpers/helpers';
import { uiIdsToNames } from '~contextProviders/uiIds/helpers';
import { LikeBoolean, toBoolean } from '~api/helpers/converters';

type Body = {
  quizId?: QUIZ_IDS; // optional because it isnt't sent on calculator page
  name?: string;
  phone?: string;
  email?: string;

  employees?: number | null;
  areaForOnePerson?: number | null;
  offices?: number | null;
  meetingRooms?: number | null;
  kitchen?: number | null;

  costFrom?: number | null;
  costTo?: number | null;
  moveTime?: number | null;
  rentTime?: number | null;
  furnish?: LikeBoolean;

  investmentFrom?: number | null;
  investmentTo?: number | null;
  revenueFrom?: number | null;
  revenueTo?: number | null;
  objectType?: ObjectType['type'] | null;

  'rooms[]'?: number[] | null;

  areaFrom?: number | null;
  areaTo?: number | null;
  'complexes[]'?: number[] | null;
  costFullFrom?: number | null;
  costFullTo?: number | null;
  furniture?: LikeBoolean;
  typeListing?: ObjectType['type_listing'] | null;

  uiIds?: UiIds;
};

type Args = {
  data: Body;
};

function oldApi({ data }: Args) {
  const oldApiData = {
    ...data,
    uiIds: void 0,
    appId: data.uiIds && data.uiIds.appIds[0],
  };

  return axios.post(
    URLS.old.quizContactForm,
    stringify(withCookieData(oldApiData, 'roistat_visit')),
  );
}

async function officeCalc({ data }: { data: Body }) {
  // FormID: office.mcity.ru, Калькулятор

  const _data = {
    email: data.email,
    phone: data.phone,
    employees: data.employees,
    area_for_one_person: data.areaForOnePerson,
    offices: data.offices,
    meeting_rooms: data.meetingRooms,
    kitchen: data.kitchen,
  };

  const result = await axios.post(
    URLS.v1.rentCalculatorContactForm,
    stringify(withCookieData(_data, 'roistat_visit')),
  );

  analytics.goal.ym({ labels: ['office.mcity.ru'] });
  analytics.goal.ym({ labels: 'office-calculator', data: _data });
  analytics.goal.ga({
    category: 'office.mcity.ru',
    action: 'form_submit',
    labels: [null, 'office-calculator'],
  });

  return result;
}

async function officeQuizRent({ data }: { data: Body }) {
  // FormID: office.mcity.ru, Квиз (аренда)

  const _data = {
    email: data.email,
    phone: data.phone,
    employees: data.employees,
    area_for_one_person: data.areaForOnePerson,
    cost_from: data.costFrom,
    cost_to: data.costTo,
    move_time: data.moveTime,
    rent_time: data.rentTime,
  };

  const result = await axios.post(
    URLS.v1.rentResultsStepContactForm,
    stringify(withCookieData(_data, 'roistat_visit')),
  );

  analytics.goal.ym({ labels: ['office.mcity.ru', 'office-quiz'] });
  analytics.goal.ym({ labels: 'office-quiz-rent', data: _data });
  analytics.goal.ga({
    category: 'office.mcity.ru',
    action: 'form_submit',
    labels: [null, 'office-quiz'],
  });

  return result;
}

async function officeCatalog({ data }: { data: Body }) {
  // FormID: office.mcity.ru, Каталог

  const _data = {
    email: data.email,
    phone: data.phone,

    area_from: data['areaFrom'],
    area_to: data['areaTo'],
    'complexes[]': data['complexes[]'],
    furnish: toBoolean(data['furnish']),
    furniture: toBoolean(data['furniture']),
    cost_meter_from: data['costFrom'],
    cost_meter_to: data['costTo'],
    cost_month_from: data['costFullFrom'],
    cost_month_to: data['costFullTo'],
    'rooms[]': data['rooms[]'],
    object_type: data['objectType'],
    type_listing: data['typeListing'],
  };

  const result = await axios.post(
    URLS.v1.searchContactForm,
    stringify(withCookieData(_data, 'roistat_visit')),
  );

  analytics.goal.ym({ labels: ['office.mcity.ru'] });
  analytics.goal.ym({ labels: 'office-search', data: _data });
  analytics.goal.ga({
    category: 'office.mcity.ru',
    action: 'form_submit',
    labels: [null, 'office-search'],
  });

  return result;
}

async function investCalc({ data }: { data: Body }) {
  // FormID: invest.mcity.ru, Калькулятор

  const _data = {
    email: data.email,
    phone: data.phone,
    employees: data.employees,
    area_for_one_person: data.areaForOnePerson,
    offices: data.offices,
    meeting_rooms: data.meetingRooms,
    kitchen: data.kitchen,
  };

  const result = await axios.post(
    URLS.v1.rentCalculatorContactForm,
    stringify(withCookieData(_data, 'roistat_visit')),
  );

  analytics.goal.ym({ labels: ['invest.mcity.ru'] });
  analytics.goal.ym({ labels: 'invest-calculator', data: _data });
  analytics.goal.ga({
    category: 'invest.mcity.ru',
    action: 'form_submit',
    labels: [null, 'invest-calculator'],
  });

  return result;
}

async function investQuizBuy({ data }: { data: Body }) {
  // FormID: invest.mcity.ru, Квиз (покупка)

  const _data = {
    email: data.email,
    phone: data.phone,
    employees: data.employees,
    area_for_one_person: data.areaForOnePerson,
    cost_from: data.costFrom,
    cost_to: data.costTo,
    move_time: data.moveTime,
    furnish: toBoolean(data.furnish),
  };

  const result = await axios.post(
    URLS.v1.buyResultsStepContactForm,
    stringify(withCookieData(_data, 'roistat_visit')),
  );

  analytics.goal.ym({ labels: ['invest.mcity.ru', 'invest-quiz'] });
  analytics.goal.ym({ labels: 'invest-quiz-buy', data: _data });
  analytics.goal.ga({
    category: 'invest.mcity.ru',
    action: 'form_submit',
    labels: [null, 'invest-quiz'],
  });

  return result;
}

async function investQuizRent({ data }: { data: Body }) {
  // FormID: invest.mcity.ru, Квиз (аренда)

  const _data = {
    email: data.email,
    phone: data.phone,
    employees: data.employees,
    area_for_one_person: data.areaForOnePerson,
    cost_from: data.costFrom,
    cost_to: data.costTo,
    move_time: data.moveTime,
    rent_time: data.rentTime,
  };

  const result = await axios.post(
    URLS.v1.rentResultsStepContactForm,
    stringify(withCookieData(_data, 'roistat_visit')),
  );

  analytics.goal.ym({ labels: ['invest.mcity.ru', 'invest-quiz'] });
  analytics.goal.ym({ labels: 'invest-quiz-rent', data: _data });
  analytics.goal.ga({
    category: 'invest.mcity.ru',
    action: 'form_submit',
    labels: [null, 'invest-quiz'],
  });

  return result;
}

async function investQuizCompany({ data }: { data: Body }) {
  // FormID: invest.mcity.ru, Квиз (арендный бизнес)

  const _data = {
    email: data.email,
    phone: data.phone,
    investment_from: data.investmentFrom,
    investment_to: data.investmentTo,
    revenue_from: data.revenueFrom,
    revenue_to: data.revenueTo,
    object_type: data.objectType,
  };

  const result = await axios.post(
    URLS.v1.investResultsStepContactForm,
    stringify(withCookieData(_data, 'roistat_visit')),
  );

  analytics.goal.ym({ labels: ['invest.mcity.ru', 'invest-quiz'] });
  analytics.goal.ym({ labels: 'invest-quiz-investment', data: _data });
  analytics.goal.ga({
    category: 'invest.mcity.ru',
    action: 'form_submit',
    labels: [null, 'invest-quiz'],
  });

  return result;
}

async function apartmentsQuiz({ data }: { data: Body }) {
  // FormID: apartments.mcity.ru, Квиз

  const _data = {
    email: data.email,
    phone: data.phone,
    name: data.name,
    'rooms[]': data['rooms[]'],
    cost_from: data.costFrom,
    cost_to: data.costTo,
    furnish: toBoolean(data.furnish),
  };

  const result = await axios.post(
    URLS.v1.apartmentsResultsStepContactForm,
    stringify(withCookieData(_data, 'roistat_visit')),
  );

  analytics.goal.ym({ labels: ['apartments.mcity.ru'] });
  analytics.goal.ym({ labels: 'apartments-quiz', data: _data });
  analytics.goal.ga({
    category: 'apartments.mcity.ru',
    action: 'form_submit',
    labels: [null, 'apartments-quiz'],
  });

  return result;
}

export default function postQuizContactForm(args: Args) {
  console.log(
    'postQuizContactForm',
    JSON.stringify(
      { ...args, data: { ...args.data, uiIds: uiIdsToNames(args.data.uiIds) } },
      null,
      2,
    ),
  );

  if (
    arrayEquals(args.data.uiIds?.appIds, [APP_IDS.office]) &&
    arrayEquals(args.data.uiIds?.componentIds, [
      COMPONENT_IDS.CalculatorSection,
      COMPONENT_IDS.Listing,
    ])
  ) {
    return officeCalc(args);
  }

  if (
    arrayEquals(args.data.uiIds?.appIds, [APP_IDS.office]) &&
    arrayEquals(args.data.uiIds?.pageIds, [PAGE_IDS.RentQuiz]) &&
    arrayContains(args.data.uiIds?.componentIds, [COMPONENT_IDS.ResultsStep]) &&
    args.data.quizId === QUIZ_IDS.rent
  ) {
    return officeQuizRent(args);
  }

  if (
    arrayEquals(args.data.uiIds?.appIds, [APP_IDS.office]) &&
    arrayEquals(args.data.uiIds?.pageIds, [PAGE_IDS.SearchPage]) &&
    arrayEquals(args.data.uiIds?.componentIds, [
      COMPONENT_IDS.SearchOffice,
      COMPONENT_IDS.Listing,
    ])
  ) {
    return officeCatalog(args);
  }

  if (
    arrayEquals(args.data.uiIds?.appIds, [APP_IDS.invest]) &&
    arrayEquals(args.data.uiIds?.pageIds, [PAGE_IDS.RentCalculator]) &&
    arrayEquals(args.data.uiIds?.componentIds, [COMPONENT_IDS.Listing])
  ) {
    return investCalc(args);
  }

  if (
    arrayEquals(args.data.uiIds?.appIds, [APP_IDS.invest]) &&
    arrayEquals(args.data.uiIds?.pageIds, [PAGE_IDS.BuyQuiz]) &&
    arrayContains(args.data.uiIds?.componentIds, [COMPONENT_IDS.ResultsStep]) &&
    args.data.quizId === QUIZ_IDS.buy
  ) {
    return investQuizBuy(args);
  }

  if (
    arrayEquals(args.data.uiIds?.appIds, [APP_IDS.invest]) &&
    arrayEquals(args.data.uiIds?.pageIds, [PAGE_IDS.RentQuiz]) &&
    arrayContains(args.data.uiIds?.componentIds, [COMPONENT_IDS.ResultsStep]) &&
    args.data.quizId === QUIZ_IDS.rent
  ) {
    return investQuizRent(args);
  }

  if (
    arrayEquals(args.data.uiIds?.appIds, [APP_IDS.invest]) &&
    arrayEquals(args.data.uiIds?.pageIds, [PAGE_IDS.CompanyQuiz]) &&
    arrayContains(args.data.uiIds?.componentIds, [
      COMPONENT_IDS.ResultsWithoutObjects,
    ]) &&
    args.data.quizId === QUIZ_IDS.company
  ) {
    return investQuizCompany(args);
  }

  if (
    arrayEquals(args.data.uiIds?.appIds, [APP_IDS.apartments]) &&
    arrayEquals(args.data.uiIds?.pageIds, [PAGE_IDS.Quiz]) &&
    arrayContains(args.data.uiIds?.componentIds, [
      COMPONENT_IDS.ResultsStep,
      COMPONENT_IDS.ResultsForm,
    ]) &&
    args.data.quizId === QUIZ_IDS.apartments
  ) {
    return apartmentsQuiz(args);
  }

  return oldApi(args);
}
